import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import Experience from '../Experience.js'

export default class WorkTable
{
    constructor()
    {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.listeners = new Set()
        this.parts = []
        this.annotationAssets = []
        this.showDimensions = false
        this.textureLoader = new THREE.TextureLoader()
        this.topFinish = 'butternut'
        this.frameFinish = 'charcoal'
        this.hasUpperShelf = true
        this.hasStorageUnit = true
        this.textureRepeat = {
            x: 0.5,
            y: 1
        }

        this.setTopFinishOptions()
        this.setFrameFinishOptions()
        this.setTopFinishTextureCache()
        this.setMaterials()
        this.setGroup()
        this.setControls()
        this.setDimensions()
        this.build()
        this.notifyChange()
    }

    setControls()
    {
        this.controls = {
            width: {
                label: 'Width',
                min: 1200,
                max: 2200,
                step: 10
            },
            depth: {
                label: 'Depth',
                min: 550,
                max: 900,
                step: 10
            },
            height: {
                label: 'Height',
                min: 680,
                max: 820,
                step: 5
            },
            storageWidth: {
                label: 'Storage Width',
                min: 320,
                max: 650,
                step: 10
            }
        }
    }

    setTopFinishOptions()
    {
        this.topFinishOptions = {
            butternut: {
                id: 'butternut',
                label: 'Butternut',
                thumbnailUrl: '/textures/wood/Butternut/diff.jpg',
                diffuseUrl: '/textures/wood/Butternut/diff.jpg',
                normalUrl: '/textures/wood/Butternut/nor.jpg',
                roughnessUrl: '/textures/wood/Butternut/arm.jpg',
                metalnessUrl: '/textures/wood/Butternut/arm.jpg'
            },
            oak: {
                id: 'oak',
                label: 'Oak',
                thumbnailUrl: '/textures/wood/Oak/diff.jpg',
                diffuseUrl: '/textures/wood/Oak/diff.jpg',
                normalUrl: '/textures/wood/Oak/nor.png',
                roughnessUrl: '/textures/wood/Oak/arm.jpg',
                metalnessUrl: '/textures/wood/Oak/metal.jpg'
            },
            rosewood: {
                id: 'rosewood',
                label: 'Rosewood',
                thumbnailUrl: '/textures/wood/Rosewood/diff.jpg',
                diffuseUrl: '/textures/wood/Rosewood/diff.jpg',
                normalUrl: '/textures/wood/Rosewood/nor.jpg',
                roughnessUrl: '/textures/wood/Rosewood/arm.jpg',
                metalnessUrl: '/textures/wood/Rosewood/arm.jpg'
            }
        }
    }

    setFrameFinishOptions()
    {
        this.frameFinishOptions = {
            charcoal: {
                id: 'charcoal',
                label: 'Charcoal',
                color: '#3d4045',
                roughness: 0.24,
                metalness: 0.96
            },
            graphite: {
                id: 'graphite',
                label: 'Graphite',
                color: '#666b72',
                roughness: 0.28,
                metalness: 0.94
            },
            bronze: {
                id: 'bronze',
                label: 'Bronze',
                color: '#6d5a4c',
                roughness: 0.32,
                metalness: 0.9
            },
            ivory: {
                id: 'ivory',
                label: 'Ivory',
                color: '#c8c4bb',
                roughness: 0.34,
                metalness: 0.84
            }
        }
    }

    setTopFinishTextureCache()
    {
        this.topFinishTextureCache = {}

        for(const [key, config] of Object.entries(this.topFinishOptions))
        {
            const diffuseMap = this.textureLoader.load(config.diffuseUrl)
            diffuseMap.colorSpace = THREE.SRGBColorSpace

            const normalMap = this.textureLoader.load(config.normalUrl)
            const roughnessMap = this.textureLoader.load(config.roughnessUrl)
            const metalnessMap = this.textureLoader.load(config.metalnessUrl)

            this.topFinishTextureCache[key] = {
                diffuseMap,
                normalMap,
                roughnessMap,
                metalnessMap
            }
        }
    }

    setDimensions(overrides = {})
    {
        const MM_TO_M = 0.001
        const mm = (value) => value * MM_TO_M

        this.dimensionsMm = {
            width: 1800,
            depth: 700,
            topThickness: 30,
            height: 750,
            legThickness: 50,
            edgeInset: 30,
            storageWidth: 450,
            storageHeight: 680,
            storageDepth: 620,
            rearShelfDepth: 150,
            rearShelfThickness: 20,
            rearShelfLift: 110,
            rearShelfInsetX: 60,
            rearShelfSupportWidth: 24,
            rearShelfSupportDepth: 90,
            topCornerRadius: 12,
            ...overrides
        }

        this.dimensionsMm.storageWidth = THREE.MathUtils.clamp(
            this.dimensionsMm.storageWidth,
            this.controls.storageWidth.min,
            Math.min(this.controls.storageWidth.max, this.dimensionsMm.width - 500)
        )

        this.dimensionsMm.storageHeight = Math.max(560, this.dimensionsMm.height - 70)
        this.dimensionsMm.rearShelfInsetX = Math.min(100, Math.max(50, this.dimensionsMm.width * 0.033))
        this.dimensionsMm.storageDepth = Math.min(this.dimensionsMm.storageDepth, this.dimensionsMm.depth - 80)
        this.dimensionsMm.topCornerRadius = Math.min(18, Math.max(8, this.dimensionsMm.topCornerRadius))

        this.dimensions = Object.fromEntries(
            Object.entries(this.dimensionsMm).map(([key, value]) => [key, mm(value)])
        )
    }

    build()
    {
        this.clearParts()
        this.setDeskTop()
        if(this.hasUpperShelf)
        {
            this.setRearShelf()
        }
        this.setLegs()
        this.setFrame()
        if(this.hasStorageUnit)
        {
            this.setStorageUnit()
        }

        if(this.showDimensions)
        {
            this.setMeasurementGuides()
        }
    }

    clearParts()
    {
        for(const part of this.parts)
        {
            this.group.remove(part)

            if(part.geometry)
            {
                part.geometry.dispose()
            }
        }

        this.parts = []

        for(const asset of this.annotationAssets)
        {
            if(asset.parent)
            {
                asset.parent.remove(asset)
            }

            if(asset.geometry)
            {
                asset.geometry.dispose()
            }

            if(asset.material)
            {
                if(Array.isArray(asset.material))
                {
                    for(const material of asset.material)
                    {
                        if(material.map)
                        {
                            material.map.dispose()
                        }

                        material.dispose()
                    }
                }
                else
                {
                    if(asset.material.map)
                    {
                        asset.material.map.dispose()
                    }

                    asset.material.dispose()
                }
            }
        }

        this.annotationAssets = []
    }

    createPart(geometry, material)
    {
        const part = new THREE.Mesh(geometry, material)
        this.parts.push(part)
        this.group.add(part)
        return part
    }

    subscribe(listener)
    {
        this.listeners.add(listener)

        return () =>
        {
            this.listeners.delete(listener)
        }
    }

    notifyChange()
    {
        const state = this.getState()

        for(const listener of this.listeners)
        {
            listener(state)
        }
    }

    updateDimensionsMm(nextDimensions)
    {
        const overrides = {}

        for(const [key, value] of Object.entries(nextDimensions))
        {
            if(!(key in this.dimensionsMm))
            {
                continue
            }

            const control = this.controls[key]
            const numericValue = Number(value)

            overrides[key] = control
                ? THREE.MathUtils.clamp(numericValue, control.min, control.max)
                : numericValue
        }

        this.setDimensions({
            ...this.dimensionsMm,
            ...overrides
        })
        this.build()
        this.notifyChange()
    }

    calculatePrice()
    {
        return this.getPriceBreakdown().total
    }

    getPriceBreakdown()
    {
        const inchesPerMeter = 39.3701
        const cubicFeetPerCubicMeter = 35.3147
        const surfaceAreaSqFt = (this.dimensions.width * inchesPerMeter) * (this.dimensions.depth * inchesPerMeter) / 144
        const storageVolumeCuFt = this.dimensions.storageWidth * this.dimensions.storageHeight * this.dimensions.storageDepth * cubicFeetPerCubicMeter
        const deskBase = 40 + surfaceAreaSqFt * 13.5
        const storageUnitPrice = this.hasStorageUnit ? storageVolumeCuFt * 12 : 0
        const upperShelfPrice = this.hasUpperShelf ? this.dimensionsMm.width * 0.035 : 0
        const heightAdjustmentPrice = Math.max(0, this.dimensionsMm.height - 720) * 0.28

        const topFinishPrices = {
            butternut: 0,
            oak: 20,
            rosewood: 35
        }
        const frameFinishPrices = {
            charcoal: 0,
            graphite: 5,
            bronze: 12,
            ivory: 10
        }

        const lineItems = [
            {
                label: 'Desk base',
                price: deskBase
            },
            {
                label: 'Upper shelf',
                price: upperShelfPrice
            },
            {
                label: 'Storage unit',
                price: storageUnitPrice
            },
            {
                label: `Top finish: ${this.topFinishOptions[this.topFinish].label}`,
                price: topFinishPrices[this.topFinish] ?? 0
            },
            {
                label: `Frame finish: ${this.frameFinishOptions[this.frameFinish].label}`,
                price: frameFinishPrices[this.frameFinish] ?? 0
            },
            {
                label: 'Height adjustment',
                price: heightAdjustmentPrice
            }
        ].filter((item) => item.price > 0 || item.label === 'Desk base')

        const total = Math.round(
            lineItems.reduce((sum, item) => sum + item.price, 0) / 5
        ) * 5

        return {
            lineItems: lineItems.map((item) => ({
                ...item,
                price: Math.round(item.price)
            })),
            total
        }
    }

    getExportData()
    {
        const priceBreakdown = this.getPriceBreakdown()
        const topFinish = this.topFinishOptions[this.topFinish]
        const frameFinish = this.frameFinishOptions[this.frameFinish]

        return {
            title: 'Work Desk',
            dimensions: [
                ['Width', `${this.dimensionsMm.width} mm`],
                ['Depth', `${this.dimensionsMm.depth} mm`],
                ['Height', `${this.dimensionsMm.height} mm`],
                ['Storage width', this.hasStorageUnit ? `${this.dimensionsMm.storageWidth} mm` : 'Not included']
            ],
            selections: [
                ['Top finish', topFinish.label],
                ['Frame finish', frameFinish.label],
                ['Upper shelf', this.hasUpperShelf ? 'Enabled' : 'Disabled'],
                ['Storage unit', this.hasStorageUnit ? 'Enabled' : 'Disabled']
            ],
            lineItems: priceBreakdown.lineItems,
            total: priceBreakdown.total
        }
    }

    getState()
    {
        return {
            dimensionsMm: { ...this.dimensionsMm },
            controls: this.controls,
            price: this.calculatePrice(),
            showDimensions: this.showDimensions,
            topFinish: this.topFinish,
            frameFinish: this.frameFinish,
            hasUpperShelf: this.hasUpperShelf,
            hasStorageUnit: this.hasStorageUnit,
            topFinishOptions: Object.values(this.topFinishOptions).map((option) => ({
                id: option.id,
                label: option.label,
                thumbnailUrl: option.thumbnailUrl
            })),
            frameFinishOptions: Object.values(this.frameFinishOptions).map((option) => ({
                id: option.id,
                label: option.label,
                color: option.color
            }))
        }
    }

    setShowDimensions(showDimensions)
    {
        this.showDimensions = Boolean(showDimensions)
        this.build()
        this.notifyChange()
    }

    setHasUpperShelf(hasUpperShelf)
    {
        this.hasUpperShelf = Boolean(hasUpperShelf)
        this.build()
        this.notifyChange()
    }

    setHasStorageUnit(hasStorageUnit)
    {
        this.hasStorageUnit = Boolean(hasStorageUnit)
        this.build()
        this.notifyChange()
    }

    setTopFinish(topFinish)
    {
        if(!this.topFinishOptions[topFinish] || this.topFinish === topFinish)
        {
            return
        }

        this.topFinish = topFinish
        this.build()
        this.notifyChange()
    }

    setFrameFinish(frameFinish)
    {
        if(!this.frameFinishOptions[frameFinish] || this.frameFinish === frameFinish)
        {
            return
        }

        this.frameFinish = frameFinish
        const finish = this.frameFinishOptions[frameFinish]
        this.materials.metal.color.set(finish.color)
        this.materials.metal.roughness = finish.roughness
        this.materials.metal.metalness = finish.metalness
        this.notifyChange()
    }

    setTextureRepeat(texture, repeatX, repeatY)
    {
        texture.wrapS = THREE.RepeatWrapping
        texture.wrapT = THREE.RepeatWrapping
        texture.center.set(0.5, 0.5)
        texture.rotation = -Math.PI * 0.5
        texture.repeat.set(repeatX, repeatY)
    }

    cloneFinishTexture(texture, repeatX, repeatY, colorSpace = null)
    {
        const clonedTexture = texture.clone()

        if(colorSpace)
        {
            clonedTexture.colorSpace = colorSpace
        }

        this.setTextureRepeat(clonedTexture, repeatX, repeatY)
        clonedTexture.needsUpdate = true

        return clonedTexture
    }

    applyBoxUVs(geometry, sizeX, sizeY, sizeZ)
    {
        const positionAttribute = geometry.attributes.position
        const normalAttribute = geometry.attributes.normal
        const uvAttribute = geometry.attributes.uv

        if(!positionAttribute || !normalAttribute || !uvAttribute)
        {
            return geometry
        }

        const halfX = sizeX * 0.5
        const halfY = sizeY * 0.5
        const halfZ = sizeZ * 0.5

        for(let i = 0; i < positionAttribute.count; i++)
        {
            const px = positionAttribute.getX(i)
            const py = positionAttribute.getY(i)
            const pz = positionAttribute.getZ(i)
            const nx = Math.abs(normalAttribute.getX(i))
            const ny = Math.abs(normalAttribute.getY(i))
            const nz = Math.abs(normalAttribute.getZ(i))

            let u = 0
            let v = 0

            if(ny >= nx && ny >= nz)
            {
                u = px + halfX
                v = pz + halfZ
            }
            else if(nx >= ny && nx >= nz)
            {
                u = pz + halfZ
                v = py + halfY
            }
            else
            {
                u = px + halfX
                v = py + halfY
            }

            uvAttribute.setXY(i, u, v)
        }

        uvAttribute.needsUpdate = true
        return geometry
    }

    createTopMaterial(finishKey)
    {
        const cachedTextures = this.topFinishTextureCache[finishKey]
        const { x: repeatX, y: repeatY } = this.textureRepeat
        const diffuseMap = this.cloneFinishTexture(cachedTextures.diffuseMap, repeatX, repeatY, THREE.SRGBColorSpace)
        const normalMap = this.cloneFinishTexture(cachedTextures.normalMap, repeatX, repeatY)
        const roughnessMap = this.cloneFinishTexture(cachedTextures.roughnessMap, repeatX, repeatY)
        const metalnessMap = this.cloneFinishTexture(cachedTextures.metalnessMap, repeatX, repeatY)

        return new THREE.MeshStandardMaterial({
            map: diffuseMap,
            normalMap,
            roughnessMap,
            metalnessMap,
            roughness: 1,
            metalness: 0.08
        })
    }

    trackAnnotationAsset(asset)
    {
        this.annotationAssets.push(asset)
        return asset
    }

    createMeasurementMaterial()
    {
        return new THREE.MeshBasicMaterial({
            color: '#20242b',
            transparent: true,
            opacity: 0.92,
            toneMapped: false
        })
    }

    createDimensionArrow(start, end)
    {
        const vector = new THREE.Vector3().subVectors(end, start)
        const length = vector.length()

        if(length <= 0.001)
        {
            return
        }

        const group = this.trackAnnotationAsset(new THREE.Group())
        const direction = vector.clone().normalize()
        const midpoint = start.clone().add(end).multiplyScalar(0.5)
        const shaftLength = Math.max(0.02, length - 0.1)
        const shaftThickness = 0.008
        const headLength = Math.min(0.05, length * 0.22)
        const headRadius = 0.018
        const up = new THREE.Vector3(0, 1, 0)
        const quaternion = new THREE.Quaternion().setFromUnitVectors(up, direction)

        if(shaftLength > 0.001)
        {
            const shaft = this.trackAnnotationAsset(new THREE.Mesh(
                new THREE.CylinderGeometry(shaftThickness, shaftThickness, shaftLength, 10),
                this.createMeasurementMaterial()
            ))
            shaft.position.copy(midpoint)
            shaft.quaternion.copy(quaternion)
            group.add(shaft)
        }

        const startHead = this.trackAnnotationAsset(new THREE.Mesh(
            new THREE.ConeGeometry(headRadius, headLength, 12),
            this.createMeasurementMaterial()
        ))
        startHead.position.copy(start).add(direction.clone().multiplyScalar(headLength * 0.5))
        startHead.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(up, direction.clone().negate()))
        group.add(startHead)

        const endHead = this.trackAnnotationAsset(new THREE.Mesh(
            new THREE.ConeGeometry(headRadius, headLength, 12),
            this.createMeasurementMaterial()
        ))
        endHead.position.copy(end).add(direction.clone().multiplyScalar(-headLength * 0.5))
        endHead.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(up, direction))
        group.add(endHead)

        this.group.add(group)
    }

    createLabelSprite(text)
    {
        const canvas = document.createElement('canvas')
        canvas.width = 320
        canvas.height = 128
        const context = canvas.getContext('2d')

        context.clearRect(0, 0, canvas.width, canvas.height)
        context.fillStyle = 'rgba(252, 249, 244, 0.96)'
        context.strokeStyle = 'rgba(32, 36, 43, 0.16)'
        context.lineWidth = 6
        this.drawRoundRect(context, 6, 6, canvas.width - 12, canvas.height - 12, 28)
        context.fill()
        context.stroke()

        context.fillStyle = '#20242b'
        context.font = '700 42px Segoe UI'
        context.textAlign = 'center'
        context.textBaseline = 'middle'
        context.fillText(text, canvas.width / 2, canvas.height / 2)

        const texture = new THREE.CanvasTexture(canvas)
        texture.needsUpdate = true

        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            toneMapped: false
        })
        const sprite = this.trackAnnotationAsset(new THREE.Sprite(material))
        sprite.scale.set(0.58, 0.232, 1)
        return sprite
    }

    drawRoundRect(context, x, y, width, height, radius)
    {
        context.beginPath()
        context.moveTo(x + radius, y)
        context.lineTo(x + width - radius, y)
        context.quadraticCurveTo(x + width, y, x + width, y + radius)
        context.lineTo(x + width, y + height - radius)
        context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
        context.lineTo(x + radius, y + height)
        context.quadraticCurveTo(x, y + height, x, y + height - radius)
        context.lineTo(x, y + radius)
        context.quadraticCurveTo(x, y, x + radius, y)
        context.closePath()
    }

    addMeasurement({ start, end, label, labelOffset })
    {
        this.createDimensionArrow(start, end)

        const sprite = this.createLabelSprite(label)
        sprite.position.copy(start).add(end).multiplyScalar(0.5).add(labelOffset)
        this.group.add(sprite)
    }

    setMeasurementGuides()
    {
        const { width, depth, height } = this.dimensions
        const topY = height
        const leftX = -width * 0.5
        const rightX = width * 0.5
        const frontZ = depth * 0.5
        const backZ = -depth * 0.5

        const widthOffsetZ = frontZ + 0.18
        const depthOffsetX = leftX - 0.18
        const heightOffsetX = rightX + 0.16

        this.addMeasurement({
            start: new THREE.Vector3(leftX, topY + 0.04, widthOffsetZ),
            end: new THREE.Vector3(rightX, topY + 0.04, widthOffsetZ),
            label: `${this.dimensionsMm.width} mm`,
            labelOffset: new THREE.Vector3(0, 0.08, 0)
        })

        this.addMeasurement({
            start: new THREE.Vector3(depthOffsetX, topY + 0.04, backZ),
            end: new THREE.Vector3(depthOffsetX, topY + 0.04, frontZ),
            label: `${this.dimensionsMm.depth} mm`,
            labelOffset: new THREE.Vector3(-0.12, 0.08, 0)
        })

        this.addMeasurement({
            start: new THREE.Vector3(heightOffsetX, 0, frontZ + 0.06),
            end: new THREE.Vector3(heightOffsetX, topY, frontZ + 0.06),
            label: `${this.dimensionsMm.height} mm`,
            labelOffset: new THREE.Vector3(0.12, 0, 0)
        })
    }

    setMaterials()
    {
        const activeFrameFinish = this.frameFinishOptions[this.frameFinish]

        this.materials = {
            woodAccent: new THREE.MeshStandardMaterial({
                color: '#c89253',
                roughness: 0.62,
                metalness: 0.08
            }),
            metal: new THREE.MeshPhysicalMaterial({
                color: activeFrameFinish.color,
                roughness: Math.max(0.16, activeFrameFinish.roughness - 0.04),
                metalness: activeFrameFinish.metalness,
                envMapIntensity: 2.05,
                clearcoat: 0.12,
                clearcoatRoughness: 0.24
            }),
            panel: new THREE.MeshStandardMaterial({
                color: '#f2efe7',
                roughness: 0.75,
                metalness: 0.03
            })
        }
    }

    setGroup()
    {
        this.group = new THREE.Group()
        this.group.position.y = 0.02
        this.scene.add(this.group)
    }

    setDeskTop()
    {
        const { width, depth, topThickness, height, topCornerRadius } = this.dimensions
        const safeRadius = Math.min(topCornerRadius, topThickness * 0.45, width * 0.08, depth * 0.08)
        const finishMaterial = this.createTopMaterial(this.topFinish)
        const deskTopGeometry = this.applyBoxUVs(
            new RoundedBoxGeometry(width, topThickness, depth, 5, safeRadius),
            width,
            topThickness,
            depth
        )
        const deskTop = this.createPart(
            deskTopGeometry,
            finishMaterial
        )

        deskTop.position.y = height - topThickness * 0.5
        deskTop.castShadow = true
        deskTop.receiveShadow = true

        this.group.add(deskTop)
    }

    setRearShelf()
    {
        const {
            width,
            depth,
            height,
            rearShelfDepth,
            rearShelfThickness,
            rearShelfLift,
            rearShelfInsetX,
            rearShelfSupportWidth,
            rearShelfSupportDepth
        } = this.dimensions

        const shelfWidth = width - rearShelfInsetX * 2
        const shelfTopY = height + rearShelfLift
        const shelfY = shelfTopY - rearShelfThickness * 0.5
        const shelfZ = -depth * 0.5 + rearShelfDepth * 0.5
        const finishMaterial = this.createTopMaterial(this.topFinish)
        const rearShelfGeometry = this.applyBoxUVs(
            new THREE.BoxGeometry(shelfWidth, rearShelfThickness, rearShelfDepth),
            shelfWidth,
            rearShelfThickness,
            rearShelfDepth
        )

        const rearShelf = this.createPart(
            rearShelfGeometry,
            finishMaterial
        )
        rearShelf.position.set(0, shelfY, shelfZ)
        rearShelf.castShadow = true
        rearShelf.receiveShadow = true
        this.group.add(rearShelf)

        const supportHeight = Math.max(0.02, shelfY - rearShelfThickness * 0.5 - height)
        const supportY = height + supportHeight * 0.5
        const supportXOffsets = [-shelfWidth * 0.35, 0, shelfWidth * 0.35]

        for(const x of supportXOffsets)
        {
            const support = this.createPart(
                new THREE.BoxGeometry(rearShelfSupportWidth, supportHeight, rearShelfSupportDepth),
                this.materials.metal
            )
            support.position.set(x, supportY, shelfZ)
            support.castShadow = true
            support.receiveShadow = true
            this.group.add(support)
        }
    }

    setLegs()
    {
        const { width, depth, topThickness, height, legThickness, edgeInset } = this.dimensions
        const legHeight = height - topThickness
        const legY = legHeight * 0.5

        const legPositions = [
            [-(width * 0.5 - legThickness * 0.5 - edgeInset), legY, -(depth * 0.5 - legThickness * 0.5 - edgeInset)],
            [-(width * 0.5 - legThickness * 0.5 - edgeInset), legY, (depth * 0.5 - legThickness * 0.5 - edgeInset)],
            [(width * 0.5 - legThickness * 0.5 - edgeInset), legY, -(depth * 0.5 - legThickness * 0.5 - edgeInset)],
            [(width * 0.5 - legThickness * 0.5 - edgeInset), legY, (depth * 0.5 - legThickness * 0.5 - edgeInset)]
        ]

        for(const position of legPositions)
        {
            const leg = this.createPart(
                new THREE.BoxGeometry(legThickness, legHeight, legThickness),
                this.materials.metal
            )
            leg.position.set(position[0], position[1], position[2])
            leg.castShadow = true
            leg.receiveShadow = true
            this.group.add(leg)
        }
    }

    setStorageUnit()
    {
        const {
            width,
            depth,
            legThickness,
            edgeInset,
            storageWidth,
            storageHeight,
            storageDepth
        } = this.dimensions

        const shellThickness = 0.05
        const sideClearance = 0.03
        const frontBackClearance = 0.03
        const rightLegInnerFaceX = width * 0.5 - legThickness - edgeInset
        const unitX = rightLegInnerFaceX - storageWidth * 0.5 - sideClearance
        const unitY = storageHeight * 0.5
        const maxDepthBetweenLegs = depth - 2 * (legThickness + edgeInset + frontBackClearance)
        const unitDepth = Math.max(0.3, Math.min(storageDepth, maxDepthBetweenLegs))
        const unitZ = 0

        const shell = this.createPart(
            new THREE.BoxGeometry(storageWidth, storageHeight, unitDepth),
            this.materials.metal
        )
        shell.position.set(unitX, unitY, unitZ)
        shell.castShadow = true
        shell.receiveShadow = true
        this.group.add(shell)

        const innerGapWidth = storageWidth - shellThickness * 2
        const innerGapHeight = storageHeight - shellThickness * 2
        const splitHeight = innerGapHeight * 0.42
        const drawerFinishMaterial = this.createTopMaterial(this.topFinish)
        const doorFinishMaterial = this.createTopMaterial(this.topFinish)
        const drawerFrontGeometry = this.applyBoxUVs(
            new THREE.BoxGeometry(innerGapWidth, splitHeight, 0.025),
            innerGapWidth,
            splitHeight,
            0.025
        )
        const doorFrontGeometry = this.applyBoxUVs(
            new THREE.BoxGeometry(innerGapWidth, innerGapHeight - splitHeight - 0.015, 0.025),
            innerGapWidth,
            innerGapHeight - splitHeight - 0.015,
            0.025
        )

        const drawerFront = this.createPart(
            drawerFrontGeometry,
            drawerFinishMaterial
        )
        drawerFront.position.set(
            unitX,
            unitY + (innerGapHeight * 0.5 - splitHeight * 0.5),
            unitZ + unitDepth * 0.5 + 0.013
        )
        drawerFront.castShadow = true
        drawerFront.receiveShadow = true

        const doorFront = this.createPart(
            doorFrontGeometry,
            doorFinishMaterial
        )
        doorFront.position.set(
            unitX,
            unitY - (splitHeight * 0.5 + 0.0075),
            unitZ + unitDepth * 0.5 + 0.013
        )
        doorFront.castShadow = true
        doorFront.receiveShadow = true

        const divider = this.createPart(
            new THREE.BoxGeometry(innerGapWidth, 0.02, 0.04),
            this.materials.metal
        )
        divider.position.set(
            unitX,
            unitY + (innerGapHeight * 0.5 - splitHeight),
            unitZ + unitDepth * 0.5 - 0.01
        )
        divider.castShadow = true

        const drawerHandle = this.createPart(
            new THREE.BoxGeometry(0.28, 0.03, 0.03),
            this.materials.metal
        )
        drawerHandle.position.set(
            unitX,
            drawerFront.position.y,
            drawerFront.position.z + 0.03
        )
        drawerHandle.castShadow = true

        const doorHandle = this.createPart(
            new THREE.BoxGeometry(0.03, 0.22, 0.03),
            this.materials.metal
        )
        doorHandle.position.set(
            unitX + innerGapWidth * 0.5 - 0.08,
            doorFront.position.y,
            doorFront.position.z + 0.03
        )
        doorHandle.castShadow = true
    }

    setFrame()
    {
        const { width, depth, topThickness, height, legThickness, edgeInset, storageWidth } = this.dimensions
        const beamHeight = 0.06
        const beamDepth = 0.04
        const beamY = height - topThickness - beamHeight * 0.5

        const innerWidth = width - 2 * (legThickness + edgeInset)
        const innerDepth = depth - 2 * (legThickness + edgeInset)

        const frontBackBeamLength = innerWidth
        const sideBeamLength = innerDepth

        const frontBeam = this.createPart(
            new THREE.BoxGeometry(frontBackBeamLength, beamHeight, beamDepth),
            this.materials.metal
        )
        frontBeam.position.set(0, beamY, depth * 0.5 - edgeInset - legThickness - beamDepth * 0.5)
        frontBeam.castShadow = true

        const backBeam = this.createPart(
            new THREE.BoxGeometry(frontBackBeamLength, beamHeight, beamDepth),
            this.materials.metal
        )
        backBeam.position.set(0, beamY, -depth * 0.5 + edgeInset + legThickness + beamDepth * 0.5)
        backBeam.castShadow = true

        const leftBeam = this.createPart(
            new THREE.BoxGeometry(beamDepth, beamHeight, sideBeamLength),
            this.materials.metal
        )
        leftBeam.position.set(-width * 0.5 + edgeInset + legThickness + beamDepth * 0.5, beamY, 0)
        leftBeam.castShadow = true

        const rightBeam = this.createPart(
            new THREE.BoxGeometry(beamDepth, beamHeight, sideBeamLength),
            this.materials.metal
        )
        rightBeam.position.set(width * 0.5 - edgeInset - legThickness - beamDepth * 0.5, beamY, 0)
        rightBeam.castShadow = true

        const innerRightFaceX = width * 0.5 - edgeInset - legThickness
        const storageCenterX = innerRightFaceX - storageWidth * 0.5 - 0.03
        const connectorLength = innerRightFaceX - storageCenterX + beamDepth * 0.25

        const storageConnector = this.createPart(
            new THREE.BoxGeometry(connectorLength, beamHeight, beamDepth),
            this.materials.metal
        )
        storageConnector.position.set(storageCenterX + connectorLength * 0.5 - beamDepth * 0.25, beamY, 0)
        storageConnector.castShadow = true
    }
}
